"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useTransition } from "react";

/**
 * Shared utility hook for URL parameter management
 *
 * Provides atomic updates to URL params, always reading from window.location
 * to avoid stale searchParams closures.
 *
 * @returns {Object} { updateParams, deleteParams, getCurrentParams }
 */
export function useUrlParams() {
  const $ = _c(10);
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const getCurrentParams = _temp;
  let t0;
  if ($[0] !== pathname || $[1] !== router) {
    t0 = (updates, t1) => {
      const options = t1 === undefined ? {} : t1;
      const {
        preserveOtherParams: t2
      } = options;
      const preserveOtherParams = t2 === undefined ? true : t2;
      startTransition(() => {
        if (typeof window === "undefined") {
          return;
        }
        const params = preserveOtherParams ? getCurrentParams() : new URLSearchParams();
        Object.entries(updates).forEach(t3 => {
          const [key, value] = t3;
          if (value === null || value === undefined || value === "") {
            params.delete(key);
          } else {
            params.set(key, String(value));
          }
        });
        const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname;
        router.replace(newUrl, {
          scroll: false
        });
      });
    };
    $[0] = pathname;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const updateParams = t0;
  let t1;
  if ($[3] !== pathname || $[4] !== router) {
    t1 = paramNames => {
      startTransition(() => {
        if (typeof window === "undefined") {
          return;
        }
        const params_0 = getCurrentParams();
        paramNames.forEach(name => params_0.delete(name));
        const newUrl_0 = params_0.toString() ? `${pathname}?${params_0.toString()}` : pathname;
        router.replace(newUrl_0, {
          scroll: false
        });
      });
    };
    $[3] = pathname;
    $[4] = router;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const deleteParams = t1;
  let t2;
  if ($[6] !== deleteParams || $[7] !== isPending || $[8] !== updateParams) {
    t2 = {
      updateParams,
      deleteParams,
      getCurrentParams,
      isPending
    };
    $[6] = deleteParams;
    $[7] = isPending;
    $[8] = updateParams;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function _temp() {
  if (typeof window === "undefined") {
    return new URLSearchParams();
  }
  return new URLSearchParams(window.location.search);
}